import React from "react";

/**
 * Marco reutilizable con estética RPG (panel de madera oscura, borde dorado,
 * cabecera con degradado + título/icono y botón de cerrar) para TODOS los
 * modales/iframes del juego (JAVATienda, Equipamiento, JAVAdex, Mochila,
 * Javamons, Actividad(es), panel/IDE Theia, Captura...).
 *
 * Da una sensación de "menú de videojuego" coherente en todo el juego.
 *
 * Uso típico (con iframe):
 *   <GameModalFrame title="JAVATienda" icon="🛒" size="lg" onClose={...} loading={loadingShop}>
 *     <iframe src=... onLoad={() => setLoading(false)} style={{width:'100%',height:'100%',border:'none'}} />
 *   </GameModalFrame>
 */

export type GameModalSize = "sm" | "md" | "lg" | "xl" | "full" | "pauseMenu";

interface GameModalFrameProps {
    title?: React.ReactNode;
    icon?: React.ReactNode;
    /** Color de acento para la cabecera (degradado) y detalles. Por defecto, madera. */
    accent?: string;
    size?: GameModalSize;
    /** Si se pasa, se muestra el botón de cerrar (✕). */
    onClose?: () => void;
    /** Texto del tooltip del botón cerrar. */
    closeHint?: string;
    /** Muestra spinner de carga por encima del contenido. */
    loading?: boolean;
    loadingText?: React.ReactNode;
    /** Si true, el contenido NO lleva padding (ideal para iframes a sangre). */
    flush?: boolean;
    /** z-index del overlay. */
    zIndex?: number;
    /** Si true, al hacer click fuera del panel se llama onClose. */
    closeOnBackdrop?: boolean;
    children?: React.ReactNode;
}

const SIZE_MAP: Record<GameModalSize, { width: string; height: string }> = {
    sm: { width: "min(460px, 82vw)", height: "min(360px, 70vh)" },
    md: { width: "min(620px, 86vw)", height: "min(460px, 76vh)" },
    lg: { width: "min(900px, 92vw)", height: "min(640px, 86vh)" },
    xl: { width: "min(1200px, 95vw)", height: "min(780px, 92vh)" },
    full: { width: "calc(100vw - 40px)", height: "calc(100vh - 40px)" },
    // Menú de pausa: un poco más alto que sm para que entren todos los botones.
    pauseMenu: { width: "min(460px, 82vw)", height: "min(440px, 80vh)" },
};

// Paleta RPG "madera + oro"
const WOOD_DARK = "#2a1d12";
const WOOD_EDGE = "#7a5c38";
const GOLD = "#c79a5c";
const PARCHMENT = "#f5e9d6";

const GameModalFrame: React.FC<GameModalFrameProps> = ({
    title,
    icon,
    accent,
    size = "md",
    onClose,
    closeHint = "Cerrar (Esc)",
    loading = false,
    loadingText,
    flush = true,
    zIndex = 20,
    closeOnBackdrop = false,
    children,
}) => {
    const dims = SIZE_MAP[size];
    const headerGradient = accent
        ? `linear-gradient(90deg, ${accent} 0%, ${WOOD_EDGE} 120%)`
        : `linear-gradient(90deg, #5a4328 0%, ${WOOD_EDGE} 100%)`;

    const showHeader = title != null || icon != null || !!onClose;

    return (
        <div
            onMouseDown={(e) => {
                if (closeOnBackdrop && onClose && e.target === e.currentTarget) onClose();
            }}
            style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex,
            }}
        >
            {/* Keyframes del spinner (por si el host no los define). */}
            <style>
                {`@keyframes gmfSpin { 0%{transform:rotate(0)} 100%{transform:rotate(360deg)} }`}
            </style>

            <div
                style={{
                    width: dims.width,
                    height: dims.height,
                    background: WOOD_DARK,
                    borderRadius: 16,
                    overflow: "hidden",
                    boxShadow: "0 18px 48px rgba(0,0,0,0.65), 0 0 0 2px rgba(0,0,0,0.3)",
                    display: "flex",
                    flexDirection: "column",
                    // Doble borde: oro exterior + madera interior.
                    border: `3px solid ${WOOD_EDGE}`,
                    outline: `2px solid ${GOLD}`,
                    outlineOffset: "-6px",
                }}
            >
                {showHeader && (
                    <div
                        style={{
                            background: headerGradient,
                            padding: "9px 14px",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            color: PARCHMENT,
                            borderBottom: `2px solid ${GOLD}`,
                            flex: "0 0 auto",
                        }}
                    >
                        <span
                            style={{
                                fontWeight: 800,
                                fontSize: 15,
                                letterSpacing: 0.5,
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                                textShadow: "0 2px 0 rgba(0,0,0,0.45)",
                                fontFamily: "'Segoe UI', sans-serif",
                            }}
                        >
                            {icon != null && <span style={{ fontSize: 17 }}>{icon}</span>}
                            {title}
                        </span>
                        {onClose && (
                            <button
                                onClick={onClose}
                                title={closeHint}
                                style={{
                                    background: "rgba(0,0,0,0.25)",
                                    border: `1px solid ${GOLD}`,
                                    color: PARCHMENT,
                                    width: 26,
                                    height: 26,
                                    borderRadius: "50%",
                                    cursor: "pointer",
                                    fontWeight: "bold",
                                    fontSize: 13,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    transition: "background 0.15s, transform 0.1s",
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.background = "#b23b3b";
                                    e.currentTarget.style.transform = "scale(1.1)";
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.background = "rgba(0,0,0,0.25)";
                                    e.currentTarget.style.transform = "scale(1)";
                                }}
                            >
                                ✕
                            </button>
                        )}
                    </div>
                )}

                <div
                    style={{
                        position: "relative",
                        flex: 1,
                        width: "100%",
                        height: "100%",
                        padding: flush ? 0 : 16,
                        boxSizing: "border-box",
                        background: WOOD_DARK,
                    }}
                >
                    {loading && (
                        <div
                            style={{
                                position: "absolute",
                                inset: 0,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                background: WOOD_DARK,
                                color: "#f0c060",
                                zIndex: 5,
                                gap: 14,
                            }}
                        >
                            <div
                                style={{
                                    width: 44,
                                    height: 44,
                                    border: "4px solid rgba(240,192,96,0.18)",
                                    borderTop: "4px solid #f0c060",
                                    borderRadius: "50%",
                                    animation: "gmfSpin 1s linear infinite",
                                }}
                            />
                            {loadingText && (
                                <div style={{ fontSize: 14, fontWeight: 600, color: PARCHMENT }}>{loadingText}</div>
                            )}
                        </div>
                    )}
                    {children}
                </div>
            </div>
        </div>
    );
};

export default GameModalFrame;
